import {AllRawTransactionsDocument} from '@transactions/graphql';
import _ from 'lodash';
import type {DataProxy} from '@apollo/client';
import type {
  AllRawTransactionsQuery,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {RawTransactionFilters} from 'src/RawTransactionFilters';

type Args = {
  client: DataProxy;
  /** The variables the page was queried with — also its cache key. */
  filters: RawTransactionFilters;
  /**
   * What a row must match to belong in the cached page, when that is wider
   * than the variables that keyed it: the Activity list pages past its own
   * date bounds, so rows outside `filters` can legitimately be on the page.
   * Defaults to `filters`.
   */
  matchFilters?: RawTransactionFilters;
};

export type Mutation = {
  toAdd?: BasicRawTransactionFragment[];
  toRemove?: string[];
  toReplace?: BasicRawTransactionFragment[];
};

// The list's rows are narrowed server-side; a row created — or edited — while
// a filter is active (date, amount, search, account, allocation,
// reconciliation) may fall outside it, and keeping such a row in the cached
// page would contradict the filter until the next reload.
const matchesFilters = (
  filters: RawTransactionFilters,
  rt: BasicRawTransactionFragment,
): boolean =>
  (!filters.startDate || rt.date >= filters.startDate) &&
  (!filters.endDate || rt.date <= filters.endDate) &&
  (!filters.search ||
    rt.description.toLowerCase().includes(filters.search.toLowerCase())) &&
  (!filters.accountId || rt.account._id === filters.accountId) &&
  (!filters.accountIds?.length || filters.accountIds.includes(rt.account._id)) &&
  // Mirrors the server's allocation clause for new-model rows: allocated
  // means fully consumed (remainingAmount 0). A legacy row has no
  // remainingAmount at all and is allocated through its `transaction`
  // linkage instead, which this fragment can't see — so a null remainder is
  // *unknown*, not unallocated, and is left for the server to judge rather
  // than being read as a mismatch.
  (filters.allocated === undefined ||
    rt.remainingAmount == null ||
    (rt.remainingAmount === 0) === filters.allocated) &&
  // Mirrors the server's reconciledAt existence check. A row a mutation just
  // created (e.g. Create inverse from the Activity list) is never reconciled,
  // so it must not land in a reconciled-only page.
  (filters.reconciled === undefined ||
    (rt.reconciliation != null) === filters.reconciled) &&
  // Mirrors the server's sign-blind magnitude bounds (both in cents).
  (filters.minAmount === undefined || Math.abs(rt.amount) >= filters.minAmount) &&
  (filters.maxAmount === undefined || Math.abs(rt.amount) <= filters.maxAmount);

const updateRawTransactions = (args: Args) => (mutation: Mutation) => {
  const {client, filters, matchFilters = filters} = args;
  const {toAdd, toRemove, toReplace} = mutation;

  const existing = client.readQuery<AllRawTransactionsQuery>({
    query: AllRawTransactionsDocument,
    variables: filters,
  });
  // A null read means the cached entry is incomplete — the mutation link
  // just evicted stale homeAmountPreview fields — and the list is about to
  // refetch. Writing "only the new rows" here would replace the entry with
  // a small COMPLETE result, silently dropping every cached row and
  // cancelling the refetch that would have restored them. Skip the in-place
  // patch; the refetch delivers the fresh list, new rows included.
  if (existing?.allRawTransactions == null) return;
  let items = existing.allRawTransactions.items;
  const id = (rt: BasicRawTransactionFragment) => rt._id;

  if (toRemove) {
    items = _.differenceWith(items, toRemove, (rt, removeId) => rt._id === removeId);
  }
  if (toReplace) {
    const replaceMap = new Map(toReplace.map((r) => [r._id, r]));
    // An edit can move a row outside the active narrowing (amount across a
    // bound, date out of range, description off a search) just as surely as a
    // create can land outside it. The server page would drop the row, so the
    // cached one must too — a replacement that no longer matches leaves
    // instead of being patched in place. Header already does this for the one
    // filter it can see (a row edited into another account leaves an
    // account-scoped list); this covers the rest.
    //
    // Matched against `matchFilters`, not `filters`: a page extended past a
    // date bound is wider than the variables that keyed it, so a row outside
    // the filter can still legitimately be on the page.
    items = items.flatMap((item) => {
      const replacement = replaceMap.get(item._id);
      if (replacement == null) return [item];
      return matchesFilters(matchFilters, replacement) ? [replacement] : [];
    });
  }
  if (toAdd) {
    items = _.unionBy(
      items,
      toAdd.filter((rt) => matchesFilters(matchFilters, rt)),
      id,
    );
  }
  items = _.clone(items);
  items.sort((a, b) => b.date.localeCompare(a.date));

  client.writeQuery({
    query: AllRawTransactionsDocument,
    variables: filters,
    data: {
      allRawTransactions: {
        __typename: 'PaginatedRawTransactions',
        items,
        startCursor: existing.allRawTransactions.startCursor ?? null,
        endCursor: existing.allRawTransactions.endCursor ?? null,
        hasMore: existing.allRawTransactions.hasMore ?? false,
      },
    },
  });
};
export default updateRawTransactions;
